/**
 * Visualizer Module Exports and Auto-Registration
 * 
 * This file exports all visualizer implementations and automatically
 * registers them with the VisualizerRegistry when imported.
 */

import { TimeVisualizer } from './implementations/TimeVisualizer';
import { WaveformVisualizer } from './implementations/WaveformVisualizer';
import { OscilloscopeVisualizer } from './implementations/OscilloscopeVisualizer';
import { OscilloscopeXYVisualizer } from './implementations/OscilloscopeXYVisualizer';
import { OscilloscopeXYWebGLVisualizer } from './implementations/OscilloscopeXYWebGLVisualizer';
import { LissajousVisualizer } from './implementations/LissajousVisualizer';
import { XYZVisualizer } from './implementations/XYZVisualizer';
import { ChladniVisualizer } from './implementations/ChladniVisualizer';
import { FluidVisualizer } from './implementations/FluidVisualizer';
import { WaterVisualizer } from './implementations/WaterVisualizer';
import { GalaxyVisualizer } from './implementations/GalaxyVisualizer';
import { SacredVisualizer } from './implementations/SacredVisualizer';
import { DMTVisualizer } from './implementations/DMTVisualizer';
import { AudioBloomVisualizer } from './implementations/AudioBloomVisualizer';

import { getVisualizerRegistry } from './registry';
import { createDualVisualizer } from './DualVisualizerFactory';

// Export types
export * from './types';
export * from './registry';
export * from './DualVisualizerFactory';

// Export visualizer classes
export {
  TimeVisualizer,
  WaveformVisualizer,
  OscilloscopeVisualizer,
  OscilloscopeXYVisualizer,
  OscilloscopeXYWebGLVisualizer,
  LissajousVisualizer,
  XYZVisualizer,
  ChladniVisualizer,
  FluidVisualizer,
  WaterVisualizer,
  GalaxyVisualizer,
  SacredVisualizer,
  DMTVisualizer,
  AudioBloomVisualizer,
};

/**
 * Initialize and register all visualizers
 * Call this function to auto-register all available visualizers
 */
export function initializeVisualizers(): void {
  const registry = getVisualizerRegistry();

  // Register all visualizers
  registry.register(new TimeVisualizer());
  registry.register(new WaveformVisualizer());
  registry.register(new OscilloscopeVisualizer());
  
  // Oscilloscope XY: Canvas/WebGL switching based on user preference
  // ONE dropdown entry that automatically selects Canvas or WebGL version
  registry.register(
    createDualVisualizer(
      'oscilloscope-xy',
      OscilloscopeXYVisualizer,
      OscilloscopeXYWebGLVisualizer
    )
  );
  
  registry.register(new LissajousVisualizer());
  registry.register(new XYZVisualizer());
  registry.register(new ChladniVisualizer());
  registry.register(new FluidVisualizer());
  registry.register(new WaterVisualizer());
  registry.register(new GalaxyVisualizer());
  registry.register(new SacredVisualizer());
  registry.register(new DMTVisualizer());
  registry.register(new AudioBloomVisualizer());

  console.log(`✅ Registered ${registry.count} visualizers`);
}

// Auto-initialize on module load
initializeVisualizers();
